import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, MapPin, Sparkles, Home, KeyRound } from "lucide-react";
import api from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import CityLinks from "@/components/CityLinks";
import { useSeo, breadcrumbLd, cityPath, propertyPath } from "@/lib/seo";
import { publicUrl } from "@/lib/publicUrl";

// Pagina geografica SEO: /case-{città} — descrizione AI in cima + immobili disponibili.
export default function CityPage({ slug }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");

    useEffect(() => {
        setLoading(true);
        api.get(`/geo/cities/${slug}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
        window.scrollTo(0, 0);
    }, [slug]);

    const items = (data?.items || []).filter((p) => !filter || p.transaction_type === filter);

    useSeo({
        title: data ? data.seo.title : `Case a ${slug} | VendiDirect`,
        description: data?.seo?.description,
        canonical: cityPath(slug),
        image: data?.items?.find((p) => (p.photos?.[0] || "").startsWith("http"))?.photos?.[0],
        jsonLd: data ? [
            breadcrumbLd([{ name: "Home", path: "/" }, { name: "Annunci", path: "/annunci" }, { name: `Case a ${data.city}`, path: cityPath(slug) }]),
            {
                "@type": "ItemList",
                name: data.seo.title,
                numberOfItems: data.items.length,
                itemListElement: data.items.slice(0, 30).map((p, i) => ({ "@type": "ListItem", position: i + 1, url: publicUrl(propertyPath(p)), name: p.title })),
            },
        ] : null,
    }, [data, slug, loading]);

    if (loading) return <div className="pt-32 w-full px-6 md:px-12 text-ink-400">Caricamento...</div>;
    if (!data) {
        return (
            <div data-testid="city-page-empty" className="pt-32 pb-24 w-full px-6 md:px-12 lg:px-20 text-center">
                <h1 className="font-serif text-4xl text-ink-50">Nessun annuncio in questa città</h1>
                <p className="text-ink-400 mt-4">Prova con un'altra città o consulta tutto il catalogo.</p>
                <Link to="/annunci" className="cta-gold inline-block mt-8 px-8 py-3 text-sm">Vai agli annunci</Link>
            </div>
        );
    }

    return (
        <div data-testid="city-page" className="pt-28 pb-24 w-full px-6 md:px-12 lg:px-20 2xl:px-28">
            <Link to="/annunci" className="inline-flex items-center gap-2 text-sm text-ink-400 hover:text-gold transition-colors mb-6">
                <ChevronLeft size={14} /> Tutti gli annunci
            </Link>

            <header className="mb-10">
                <div className="flex items-center gap-2 text-xs text-ink-400 uppercase tracking-wider mb-3">
                    <MapPin size={14} className="text-gold" /> {data.region || "Italia"}
                </div>
                <h1 data-testid="city-title" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink-50 leading-tight">
                    Case in vendita e affitto a <span className="italic text-gold">{data.city}</span>
                </h1>
            </header>

            <section data-testid="city-ai-description" className="news-card-neu p-6 md:p-8 mb-12 max-w-4xl">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-blue-700 mb-3">
                    <Sparkles size={14} /> {data.ai_generated ? "Vivere a" : "Scopri"} {data.city}
                </div>
                <p className="text-base md:text-lg leading-relaxed" style={{ color: "#1E293B" }}>{data.description}</p>
                <div className="flex flex-wrap gap-3 mt-6">
                    <button data-testid="city-filter-all" onClick={() => setFilter("")} className={`neu-btn px-4 py-2 text-sm font-medium ${filter === "" ? "neu-btn-active" : ""}`}>
                        Tutti · {data.count}
                    </button>
                    <button data-testid="city-filter-vendita" onClick={() => setFilter("vendita")} className={`neu-btn inline-flex items-center gap-2 px-4 py-2 text-sm font-medium ${filter === "vendita" ? "neu-btn-active" : ""}`}>
                        <Home size={14} /> In vendita · {data.vendita}
                    </button>
                    <button data-testid="city-filter-affitto" onClick={() => setFilter("affitto")} className={`neu-btn inline-flex items-center gap-2 px-4 py-2 text-sm font-medium ${filter === "affitto" ? "neu-btn-active" : ""}`}>
                        <KeyRound size={14} /> In affitto · {data.affitto}
                    </button>
                </div>
            </section>

            <section>
                <span className="label-eyebrow">Immobili disponibili</span>
                <h2 className="font-serif text-base md:text-lg text-ink-50 mt-2 mb-8">{items.length} annunci a {data.city}</h2>
                {items.length === 0 ? (
                    <div className="text-ink-400 text-sm">Nessun annuncio per questo filtro.</div>
                ) : (
                    <div data-testid="city-grid" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-14">
                        {items.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
                    </div>
                )}
            </section>

            {data.other_cities?.length > 0 && (
                <section className="mt-24">
                    <span className="label-eyebrow">Esplora altre città</span>
                    <div className="mt-6"><CityLinks cities={data.other_cities} exclude={slug} /></div>
                </section>
            )}
        </div>
    );
}
